import { create } from 'zustand';
import { User, Organization } from '@/types';
import { authService } from '@/services/auth.service';

interface AuthState {
  user: User | null;
  organization: Organization | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  setUser: (user: User | null) => void;
  setOrganization: (organization: Organization | null) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, organization_name?: string, referral_code?: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchCurrentUser: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  organization: null,
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,

  setUser: (user) => set({ user, isAuthenticated: !!user }),

  setOrganization: (organization) => set({ organization }),

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login({ email, password });
      set({
        user: response.user,
        organization: response.organization,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      set({ error: errorMessage, isLoading: false, isAuthenticated: false });
      throw error;
    }
  },

  register: async (email, password, name, organization_name?, referral_code?) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.register({ email, password, name, ...(organization_name ? { organization_name } : {}), ...(referral_code ? { referral_code } : {}) });
      set({
        user: response.user,
        organization: response.organization,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      set({ error: errorMessage, isLoading: false, isAuthenticated: false });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
    } finally {
      set({
        user: null,
        organization: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  fetchCurrentUser: async () => {
    if (!authService.isAuthenticated()) {
      set({ isAuthenticated: false, user: null, organization: null });
      return;
    }

    set({ isLoading: true });
    try {
      const data = await authService.getCurrentUser();
      set({
        user: data.user,
        organization: data.organization,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        user: null,
        organization: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  clearError: () => set({ error: null }),
}));
